import { useReducer, useCallback, type ReactNode } from 'react';
import {
  KioskContext,
  type KioskContextValue,
  type InfoVariant,
  initialState,
  kioskReducer,
} from './kioskState';

interface KioskProviderProps {
  kioskCode: string;
  locationId: string;
  locationName: string;
  children: ReactNode;
}

export function KioskProvider({ kioskCode, locationId, locationName, children }: KioskProviderProps) {
  const [state, dispatch] = useReducer(kioskReducer, {
    ...initialState,
    kioskCode,
    locationId,
    locationName,
  });

  const scanEquipment = useCallback(
    (equipmentId: string) => dispatch({ type: 'EQUIPMENT_SCANNED', equipmentId }),
    []
  );
  const scanUser = useCallback(
    (userId: string) => dispatch({ type: 'USER_SCANNED', userId }),
    []
  );
  const routeCheckout = useCallback(() => dispatch({ type: 'ROUTE_CHECKOUT' }), []);
  const routeCheckin = useCallback(() => dispatch({ type: 'ROUTE_CHECKIN' }), []);
  const showSuccess = useCallback(
    (message: string) => dispatch({ type: 'SUCCESS', message }),
    []
  );
  const showInfo = useCallback(
    (message: string, variant: InfoVariant, details?: string) =>
      dispatch({ type: 'SHOW_INFO', message, variant, ...(details !== undefined && { details }) }),
    []
  );
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  const value: KioskContextValue = {
    state,
    scanEquipment,
    scanUser,
    routeCheckout,
    routeCheckin,
    showSuccess,
    showInfo,
    reset,
  };

  return (
    <KioskContext.Provider value={value}>{children}</KioskContext.Provider>
  );
}
